/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ITeachingBubbleProps, TeachingBubble } from '@fluentui/react/lib/TeachingBubble';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { getEventBus } from 'src/core/utils/eventBus';
import { TeachingBubbleDto, TeachingBubblesKeys } from 'src/luis/projects/events/types/TeachingBubbleDto';

type TeachingBubbleProps = ITeachingBubbleProps & { children: React.ReactNode; key: TeachingBubblesKeys };

const messages = defineMessages({
    gotIt: {
        id: 'events.teachingBubbleOrchestrator.gotIt',
        defaultMessage: 'Got it'
    },
    skip: {
        id: 'events.teachingBubbleOrchestrator.skip',
        defaultMessage: 'Skip all'
    },
    dismissButtonAria: {
        id: 'events.teachingBubbleOrchestrator.dismissButtonAria',
        defaultMessage: 'Close'
    }
});

const eventBus = getEventBus();

/**
 * Orchestrator component that manages showing and hiding teaching bubbles.
 * It also ensures that already shown teaching bubbles don't appear again.
 */
export const TeachingBubbleOrchestrator = observer(() => {
    const { uiStore, dispatcher } = useGlobalState();
    const { localize } = useLocalization();

    const priorityMap = React.useRef(new Map<TeachingBubblesKeys, NodeJS.Timeout>());
    const [visibleTeachingBubbleProps, setVisibleTeachingBubbleProps] = React.useState<TeachingBubbleProps>();
    const [queue, setQueue] = React.useState<TeachingBubbleProps[]>([]);
    const openTeachingBubblesSet = React.useRef<Set<TeachingBubblesKeys>>(new Set());

    const handleDismiss = React.useCallback(
        (key: TeachingBubblesKeys) => {
            if (openTeachingBubblesSet.current.has(key)) {
                getLogger().endEvent(`${key}TeachingBubble`, false);
                setQueue(_queue => _queue.filter(item => item.key !== key));
                dispatcher.dispatch('markTeachingBubbleAsViewed', { bubbleName: key });
                priorityMap.current.delete(key);
                openTeachingBubblesSet.current.delete(key);
            }
        },
        [dispatcher.dispatch]
    );

    const convertDtoToProps = React.useCallback(
        (dto: TeachingBubbleDto): TeachingBubbleProps => ({
            key: dto.key,
            target: dto.config.target,
            children: dto.message.body,
            headline: dto.message.title,
            primaryButtonProps: dto.hasGotIt
                ? {
                      children: localize(messages.gotIt),
                      onClick: () => {
                          getLogger().endEvent(`${dto.key}TeachingBubble`, false);
                          handleDismiss(dto.key);
                      },
                      ariaLabel: localize(messages.gotIt),
                      uniqueId: 'Got it',
                      id: 'Got it'
                  }
                : null,
            secondaryButtonProps: dto.hasSkip
                ? {
                      children: localize(messages.skip),
                      onClick: () => {
                          getLogger().endEvent(`${dto.key}TeachingBubble`, false);
                          dispatcher.dispatch('markTeachingBubbleAsSkipped', { bubbleName: dto.key });
                          handleDismiss(dto.key);
                      },
                      ariaLabel: localize(messages.skip),
                      uniqueId: 'Skip all',
                      id: 'Skip all'
                  }
                : null,
            onDismiss: () => {
                getLogger().endEvent(`${dto.key}TeachingBubble`, false);
                dto.onDismiss ? dto.onDismiss() : handleDismiss(dto.key);
            },
            hasCondensedHeadline: true,
            styles: {
                bodyContent: {
                    lineHeight: 18
                }
            },
            calloutProps: {
                setInitialFocus: false,
                preventDismissOnEvent: (_ev: Event | React.FocusEvent | React.KeyboardEvent | React.MouseEvent) => true,
                ...(dto.config.calloutProps ?? {})
            },
            hasCloseButton: Boolean(dto.onDismiss),
            closeButtonAriaLabel: localize(messages.dismissButtonAria)
        }),
        [handleDismiss]
    );

    React.useEffect(
        () =>
            eventBus.on('queueTeachingBubble', teachingBubbleDTO => {
                const viewedBefore = uiStore.teachingBubbles[teachingBubbleDTO.key];
                if (!viewedBefore && teachingBubbleDTO.config.target && !openTeachingBubblesSet.current.has(teachingBubbleDTO.key)) {
                    const teachingBubbleProps = convertDtoToProps(teachingBubbleDTO);

                    if (priorityMap.current.has(teachingBubbleDTO.key)) {
                        clearTimeout(priorityMap.current.get(teachingBubbleDTO.key));
                    }

                    getLogger().startEvent(`${teachingBubbleDTO.key}TeachingBubble`);
                    priorityMap.current.set(
                        teachingBubbleDTO.key,
                        setTimeout(() => {
                            if (!uiStore.teachingBubbles[teachingBubbleDTO.key]) {
                                setQueue(_queue => [..._queue, teachingBubbleProps]);
                                priorityMap.current.delete(teachingBubbleDTO.key);
                            }
                        }, teachingBubbleDTO.config.delay ?? 0)
                    );
                    openTeachingBubblesSet.current.add(teachingBubbleDTO.key);
                }
            }),
        [convertDtoToProps]
    );

    React.useEffect(() => eventBus.on('dequeueTeachingBubble', handleDismiss), [handleDismiss]);

    React.useEffect(() => {
        setVisibleTeachingBubbleProps(queue[0]);
    }, [queue]);

    // Renders teaching bubbles with different keys each time `visibleTeachingBubbleProps` changes to get the default
    // teaching bubble animation between rendering two teaching bubbles.
    return React.useMemo(() => (visibleTeachingBubbleProps ? <TeachingBubble key={getId()} {...visibleTeachingBubbleProps} /> : null), [
        visibleTeachingBubbleProps
    ]);
});

TeachingBubbleOrchestrator.displayName = 'TeachingBubbleOrchestrator';
